import type {
  AttachmentAdapter,
  CompleteAttachment,
  CreateAttachment,
  PendingAttachment,
} from "@assistant-ui/react";

import { isAxiosError } from "axios";

import api, { UPLOAD_REQUEST_TIMEOUT_MS, uploadErrorText } from "@/services/api";

import { agentClient } from "../agentClient";

/** What the attachments endpoint takes; the server types the bytes, this only filters the picker. */
export const ATTACHMENT_ACCEPT =
  "application/pdf,image/png,image/jpeg,image/gif,image/webp,text/plain,text/csv";

const PREFIX = "tf:";

/** A file already on the thread, as a composer attachment: nothing uploads when it is added. */
export function threadFileAttachment(file: {
  id: number;
  name: string;
  contentType: string;
}): CreateAttachment {
  return {
    id: `${PREFIX}${file.id}`,
    type: file.contentType.startsWith("image/") ? "image" : "document",
    name: file.name,
    contentType: file.contentType,
    content: [],
  };
}

/** The thread file id of an attachment we uploaded ("tf:<id>"), or null. */
export function threadFileId(attachmentId: string): number | null {
  if (!attachmentId.startsWith(PREFIX)) return null;
  const id = Number(attachmentId.slice(PREFIX.length));
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * This page's uploads by attachment id, with the thread file each became (null while it uploads).
 * One table for every runtime of the tab, so any view can tell which file an upload's chip shows.
 */
const uploads = new Map<string, { threadId: number; fileId: number | null }>();
let uploadCount = 0;

/** The PDFs uploaded from this page, by the thread file each became, until their pages are counted. */
const uploadedPdfs = new Map<number, File>();

/** An uploaded PDF's own file, once: its pages are counted from it, not fetched again. */
export function takeUploadedPdf(fileId: number): File | undefined {
  const file = uploadedPdfs.get(fileId);
  uploadedPdfs.delete(fileId);
  return file;
}

/** The thread file a composer attachment shows: "tf:<id>", or an upload once it finished. */
export function attachmentFileId(attachmentId: string): number | null {
  return threadFileId(attachmentId) ?? uploads.get(attachmentId)?.fileId ?? null;
}

/** An upload whose file left the draft in another view: its chip goes, nothing is taken back. */
export function forgetUpload(attachmentId: string): void {
  uploads.delete(attachmentId);
}

/**
 * Takes back a file the composer held and never sent: the thread's link goes, the stored file
 * stays. A sent file (an @-pick from this chat, a re-upload of a sent file) keeps its link: no
 * request when a loaded message carries it, and the server's 409 when one beyond them does.
 * Never throws (the chip is already gone): a failure leaves an unsent link behind and is logged.
 */
export async function deleteUnsentThreadFile(threadId: number, fileId: number): Promise<void> {
  if (agentClient.isFileSent(threadId, fileId)) return;
  try {
    await api.delete(`/agent/threads/${threadId}/files/${fileId}/`);
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 409) return;
    // eslint-disable-next-line no-console -- the asked-for trace of a link left behind; no UI
    console.warn(`Could not take back thread file ${fileId}`, error);
  }
}

type TakeBack = { fileId: number; done: Promise<void> };

/** This tab's take-backs still going, by thread (each drops out as it ends). */
const takeBacks = new Map<number, Set<TakeBack>>();
/** Each `settleTakeBacks` caller's list of the take-backs begun since it asked, by thread. */
const watchers = new Map<number, Set<TakeBack[]>>();

/** Takes a file off the draft in every tab, then takes back its link if it was never sent. */
export async function takeBackThreadFile(threadId: number, fileId: number): Promise<void> {
  agentClient.detachFile(threadId, fileId);
  const takeBack = { fileId, done: deleteUnsentThreadFile(threadId, fileId) };
  const going = takeBacks.get(threadId) ?? new Set();
  takeBacks.set(threadId, going.add(takeBack));
  watchers.get(threadId)?.forEach((begun) => begun.push(takeBack));
  await takeBack.done;
  going.delete(takeBack);
  if (going.size === 0 && takeBacks.get(threadId) === going) takeBacks.delete(threadId);
}

/**
 * Waits for the thread's take-backs going, before a file is shared into it again: the server
 * answers a share with the link it already has, which a take-back still going would then delete.
 * `since` then gives the take-back of a file begun after this call (ended or not), or null, until
 * `stop`.
 */
export async function settleTakeBacks(threadId: number) {
  const begun: TakeBack[] = [];
  const watching = watchers.get(threadId) ?? new Set();
  watchers.set(threadId, watching.add(begun));
  await Promise.all([...(takeBacks.get(threadId) ?? [])].map((takeBack) => takeBack.done));
  return {
    since: (fileId: number) => begun.find((takeBack) => takeBack.fileId === fileId)?.done ?? null,
    stop: () => {
      watching.delete(begun);
      if (watching.size === 0 && watchers.get(threadId) === watching) watchers.delete(threadId);
    },
  };
}

/** How a held file's wait ended: added again where the thread is, taken off, or the view lost. */
type HeldOutcome = "handed" | "removed" | "lost";

/** A file picked before its view's chat had a thread, waiting for it. */
export interface HeldFile {
  file: File;
  settle: (outcome: HeldOutcome) => void;
}

/**
 * One view's held files by attachment id. The view's runtime is keyed by its thread, so the one a
 * file was picked in (no thread yet) goes once the thread is known; the next adds them again.
 */
export type HeldFiles = Map<string, HeldFile>;

/** Ends every wait in `held` with `outcome` and empties it. */
export function settleHeldFiles(held: HeldFiles, outcome: HeldOutcome): HeldFile[] {
  const files = [...held.values()];
  held.clear();
  for (const entry of files) entry.settle(outcome);
  return files;
}

/**
 * Uploads a dropped or picked file to its thread at once (POST threads/<id>/attachments/), so a
 * send only names it. Once uploaded, the file joins the thread's draft and shows in every tab;
 * until then it stays in this one. A file picked before the thread is known is held in `held`,
 * shown as uploading, for the thread's runtime to add again; if the view loses its chat first,
 * the attachment fails, not the composer.
 */
export function createAttachmentAdapter(
  threadId: () => number | null,
  held: HeldFiles = new Map()
): AttachmentAdapter {
  return {
    accept: ATTACHMENT_ACCEPT,
    async *add({ file }) {
      const local: PendingAttachment = {
        id: `upload-${++uploadCount}`,
        type: file.type.startsWith("image/") ? "image" : "document",
        name: file.name,
        contentType: file.type,
        file,
        status: { type: "running", reason: "uploading", progress: 0 },
      };
      const thread = threadId();
      if (thread == null) {
        const outcome = new Promise<HeldOutcome>((settle) => held.set(local.id, { file, settle }));
        yield local;
        if ((await outcome) === "lost") {
          yield {
            ...local,
            status: { type: "incomplete", reason: "error", message: "Try again." },
          };
        }
        return;
      }
      // Registered before the chip shows, so a remove while it uploads always finds it.
      uploads.set(local.id, { threadId: thread, fileId: null });
      yield local;
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post<{ id: number; name: string; content_type: string }>(
          `/agent/threads/${thread}/attachments/`,
          form,
          { timeout: UPLOAD_REQUEST_TIMEOUT_MS, headers: { "Content-Type": "multipart/form-data" } }
        );
        const upload = uploads.get(local.id);
        if (!upload) {
          // The chip was removed before the upload answered.
          void deleteUnsentThreadFile(thread, data.id);
          return;
        }
        upload.fileId = data.id;
        if (data.content_type === "application/pdf") uploadedPdfs.set(data.id, file);
        agentClient.attachFile(thread, {
          id: data.id,
          name: data.name,
          contentType: data.content_type,
        });
        yield {
          ...local,
          name: data.name,
          contentType: data.content_type,
          status: { type: "requires-action", reason: "composer-send" },
        };
      } catch (error) {
        uploads.delete(local.id);
        const message = uploadErrorText(error, file.name);
        yield { ...local, status: { type: "incomplete", reason: "error", message } };
      }
    },
    // Only unsent uploads come here (assistant-ui never removes a complete attachment). The chip
    // goes at once; the file is taken back behind it. One still uploading is taken back when it
    // answers; one already forgotten (its file left the draft elsewhere) has nothing to take back.
    async remove(attachment) {
      held.get(attachment.id)?.settle("removed");
      held.delete(attachment.id);
      const upload = uploads.get(attachment.id);
      uploads.delete(attachment.id);
      if (upload?.fileId == null) return;
      uploadedPdfs.delete(upload.fileId); // held for a page count no longer wanted
      await takeBackThreadFile(upload.threadId, upload.fileId);
    },
    async send(attachment): Promise<CompleteAttachment> {
      const id = uploads.get(attachment.id)?.fileId;
      uploads.delete(attachment.id);
      if (id == null) throw new Error("The file did not upload.");
      return { ...attachment, id: `${PREFIX}${id}`, status: { type: "complete" }, content: [] };
    },
  };
}
